import { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Archive, Search, MapPin, Calendar, Banknote, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUR = { GBP: '£', EUR: '€', USD: '$' };

const fmtD = (d) => { try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d || ''; } };

export default function EmployeeArchivedEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/employee/archived-events`)
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = events.filter((e) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (e.location || '').toLowerCase().includes(s) || (e.position || '').toLowerCase().includes(s);
  });

  return (
    <EmployeeLayout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="employee-archived-events-page">
        <div className="border-b border-border pb-5">
          <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Archive className="w-6 h-6 text-primary" strokeWidth={1.5} />
            ARCHIVED EVENTS
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Past events — location, period and hourly pay rates.
          </p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by event or location..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 bg-background border-border rounded-sm"
            data-testid="emp-archive-search"
          />
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading…</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-10 text-center space-y-2">
              <Archive className="w-10 h-10 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">
                {q ? 'No archived events match your search.' : 'No archived events yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e, i) => {
              const cur = CUR[e.currency] || '£';
              const rateEntries = Object.entries(e.rates || {}).filter(([, v]) => v);
              return (
                <Card key={i} className="bg-card border-border rounded-sm" data-testid={`emp-archived-event-${i}`}>
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-base font-barlow tracking-wider font-semibold flex items-center gap-2 break-words">
                      <Briefcase className="w-4 h-4 shrink-0 text-muted-foreground" />
                      {e.position || 'Event'}
                    </h3>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                      <span className="break-words">{e.location || '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span>
                        {e.days} day{e.days !== 1 ? 's' : ''} •{' '}
                        {e.first_date === e.last_date ? fmtD(e.first_date) : `${fmtD(e.first_date)} – ${fmtD(e.last_date)}`}
                      </span>
                    </div>
                    <div className="border-t border-border/60 pt-2 space-y-1.5" data-testid={`emp-event-rates-${i}`}>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <Banknote className="w-3.5 h-3.5 text-primary/70" /> Hourly Pay
                      </p>
                      {rateEntries.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {rateEntries.map(([role, v]) => (
                            <Badge key={role} className="border bg-green-500/10 text-green-400 border-green-500/30 text-[11px]">
                              {role}: {cur}{v}/h
                            </Badge>
                          ))}
                        </div>
                      ) : e.hourly_payment ? (
                        <Badge className="border bg-green-500/10 text-green-400 border-green-500/30 text-[11px]">
                          {cur}{e.hourly_payment}/h
                        </Badge>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Not specified</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}
